import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { capture } from "../lib/analytics";
import {
  CONTACT_KINDS,
  CONTACT_OUTCOMES,
  ContactEntry,
  ContactKind,
  ContactOutcome,
  contactKindLabel,
  contactOutcomeLabel,
  dueLabel,
  todayYmd,
} from "../lib/contactLog";

// Contact log on the property panel (#3208): who you called or mailed, what
// happened, and when to follow up. Private to the account.

interface Props {
  propertyId: string;
}

const ContactLog: React.FC<Props> = ({ propertyId }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<ContactEntry[] | null>(null);
  const [error, setError] = useState("");
  const [kind, setKind] = useState<ContactKind>("call");
  const [outcome, setOutcome] = useState<ContactOutcome | "">("");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ data: ContactEntry[] }>("/contacts", { params: { propertyId } });
      setEntries(data.data || []);
      setError("");
    } catch {
      setError("Couldn't load the contact log.");
    }
  }, [propertyId]);

  useEffect(() => {
    if (open && entries === null) load();
  }, [open, entries, load]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await apiClient.post("/contacts", {
        propertyId,
        kind,
        outcome: outcome || null,
        note,
        followUpOn: followUp || null,
      });
      capture("contact_log_saved", { kind, has_follow_up: !!followUp });
      setNote("");
      setOutcome("");
      setFollowUp("");
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.error === "contact_log_full"
          ? "Your contact log is full. Delete old entries to add more."
          : "Couldn't save that entry. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.delete(`/contacts/${id}`);
      setEntries((es) => (es || []).filter((e) => e.id !== id));
    } catch {
      setError("Couldn't delete that entry.");
    }
  };

  const markDone = async (id: string) => {
    try {
      await apiClient.patch(`/contacts/${id}/done`);
      setEntries((es) => (es || []).map((e) => (e.id === id ? { ...e, doneAt: new Date().toISOString() } : e)));
    } catch {
      setError("Couldn't update that follow-up.");
    }
  };

  const today = todayYmd();

  return (
    <section className="contact-log" aria-label="Contact log">
      <button type="button" className="lv-chip" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Contact log {entries && entries.length > 0 ? `(${entries.length})` : ""} {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="contact-log-body">
          <div className="contact-log-form">
            <select
              className="lv-chip"
              aria-label="Type of contact"
              value={kind}
              onChange={(e) => setKind(e.target.value as ContactKind)}
            >
              {CONTACT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            <select
              className="lv-chip"
              aria-label="Outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as ContactOutcome | "")}
            >
              <option value="">Outcome…</option>
              {CONTACT_OUTCOMES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="contact-log-followup">
              Follow up
              <input
                type="date"
                className="lv-chip"
                aria-label="Follow-up date"
                min={today}
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
              />
            </label>
            <textarea
              className="contact-log-note"
              aria-label="Note"
              maxLength={1000}
              placeholder="What was said, who you spoke to…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
            <button type="button" className="lv-chip" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Add entry"}
            </button>
          </div>

          {error && (
            <p className="contact-log-error" role="alert">
              {error}
            </p>
          )}

          {entries && entries.length === 0 && <p className="contact-log-empty">No contact logged yet.</p>}
          <ul className="contact-log-list">
            {(entries || []).map((e) => (
              <li key={e.id} className={e.doneAt ? "contact-log-item contact-log-item--done" : "contact-log-item"}>
                <div>
                  <strong>{contactKindLabel(e.kind)}</strong>
                  {e.outcome ? ` · ${contactOutcomeLabel(e.outcome)}` : ""}
                  <span className="contact-log-when">
                    {" "}
                    {new Date(e.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                </div>
                {e.note && <div className="contact-log-text">{e.note}</div>}
                {e.followUpOn && (
                  <div className="contact-log-due">
                    {e.doneAt ? "Follow-up done" : `Follow up ${dueLabel(e.followUpOn, today)} (${e.followUpOn})`}
                    {!e.doneAt && (
                      <button type="button" className="map-buybox-clear" onClick={() => markDone(e.id)}>
                        Mark done
                      </button>
                    )}
                  </div>
                )}
                <button type="button" className="map-buybox-clear" aria-label="Delete entry" onClick={() => remove(e.id)}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ContactLog;
